import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, IconButton, Tooltip, Typography
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';

import { Header, Popup } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { useDispatch, useSelector } from '../../redux/hooks';
import { fetchAllWatchlists, selectAllWatchlists } from '../../redux/actions/watchlists';
import { fetchAllOpenJobs, fetchAllOpenCapacities, forgetWatchlistInMarket } from '../../redux/actions/market';
import { deleteWatchlist } from '../../api/watchlists';
import toast from '../../utils/toast';
import { WatchlistForListType } from '../../types';

import {
    formatNotificationCount, getDeleteConfirmation, getNotificationMode, getWatchlistTags, hasRoute
} from './formatWatchlist';
import CollapsibleSection from './parts/CollapsibleSection';
import ListRow from './parts/ListRow';
import NotificationModeControl from './parts/NotificationModeControl';
import NotificationWarning from './parts/NotificationWarning';
import TagChip from './parts/TagChip';
import useWatchlistNotificationMode from './parts/useWatchlistNotificationMode';

type SectionType = {
    title: string,
    items: WatchlistForListType[]
};

function getSections(watchlists: WatchlistForListType[]): SectionType[] {
    // Route watchlists stay under routes even when they also filter on a group
    const isGroup = (w: WatchlistForListType) => w.source === 'group' || (w.source === 'user' && !!w.groupId);
    const routes = watchlists.filter((w) => !isGroup(w) && hasRoute(w));
    const groups = watchlists.filter(isGroup);
    const own = watchlists.filter((w) => !isGroup(w) && !hasRoute(w));

    return [
        { title: 'Våra rutter', items: routes },
        { title: 'Grupper', items: groups },
        { title: 'Egna', items: own }
    ].filter((section) => section.items.length > 0);
}

function formatSecondary(w: WatchlistForListType) {
    const parts = [formatNotificationCount(w.notificationCount30d || 0)];
    const kinds = [w.isJob && 'Uppdrag', w.isCapacity && 'Kapacitet'].filter(Boolean).join(' & ');
    if (kinds) {
        parts.push(kinds);
    }
    if (w.takesOffer) {
        parts.push('Utan externa uppdrag');
    }
    return parts.join(' · ');
}

function WatchlistRow({ watchlist }: { watchlist: WatchlistForListType }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { select, isSaving } = useWatchlistNotificationMode(watchlist);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const name = watchlist.name || 'bevakningen';
    const confirmation = getDeleteConfirmation(watchlist);

    const confirmDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteWatchlist(watchlist.id);
            toast('Bevakning borttagen');
            dispatch(forgetWatchlistInMarket(watchlist.id));
            await dispatch(fetchAllWatchlists());
        } catch (e) {
            toast(e, 'Kunde inte ta bort bevakningen, försök igen');
            setIsDeleting(false);
        }
    };

    const pauseInstead = async () => {
        setConfirmOpen(false);
        await select('off');
    };

    return (
        <>
            <ListRow
                title={watchlist.name || 'Namnlös bevakning'}
                dimmed={!watchlist.isActive}
                tags={getWatchlistTags(watchlist).map((tag) => (
                    <TagChip key={tag} label={tag} outlined={tag === 'Pausad'} />
                ))}
                secondary={formatSecondary(watchlist)}
                onClick={() => navigate(`/watchlists/${watchlist.id}`)}
                action={(
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0
                    }}
                    >
                        <NotificationModeControl
                            value={getNotificationMode(watchlist)}
                            onSelect={select}
                            disabled={isSaving}
                            label={`Notiser för ${name}`}
                        />
                        <Tooltip title="Ta bort">
                            <IconButton
                                size="small"
                                aria-label={`Ta bort ${name}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmOpen(true);
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            />
            <Popup
                open={confirmOpen}
                title={confirmation.title}
                body={confirmation.body}
                okLabel="Sätt till Av"
                handleOk={confirmation.canPause ? pauseInstead : undefined}
                handleDelete={confirmDelete}
                handleClose={() => setConfirmOpen(false)}
                isSubmitting={isDeleting}
            />
        </>
    );
}

export default function Watchlists() {
    const dispatch = useDispatch();
    const watchlists = useSelector(selectAllWatchlists());
    const sections = React.useMemo(() => getSections(watchlists), [watchlists]);

    const activeCount = watchlists.filter((w) => w.isActive).length;
    const summary = watchlists.length
        ? `${watchlists.length} ${watchlists.length === 1 ? 'bevakning' : 'bevakningar'} · ${activeCount} ${activeCount === 1 ? 'aktiv' : 'aktiva'}`
        : undefined;

    React.useEffect(() => {
        dispatch(fetchAllWatchlists());
        dispatch(fetchAllOpenJobs(true)); // Only fetch if not recently fetched
        dispatch(fetchAllOpenCapacities(true)); // Only fetch if not recently fetched
    }, [dispatch]);

    return (
        <Container>
            <Header title="Bevakningar" subtitle={summary} addUrl="/watchlists/create" />
            <NotificationWarning />

            {sections.length === 0 && (
                <Paper>
                    <Typography>Du har inga bevakningar.</Typography>
                </Paper>
            )}

            {sections.map((section) => (
                <Paper key={section.title} padding={0} sx={{ mb: 2, overflow: 'hidden' }}>
                    <CollapsibleSection
                        title={section.title}
                        count={section.items.length}
                        storageKey={`watchlists.section.${section.title}`}
                    >
                        {section.items.map((watchlist) => (
                            <WatchlistRow key={watchlist.id} watchlist={watchlist} />
                        ))}
                    </CollapsibleSection>
                </Paper>
            ))}
        </Container>
    );
}
